import { Create } from "@refinedev/mui";
import {
  Box,
  TextField,
  Grid,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
} from "@mui/material";
import { useForm } from "@refinedev/react-hook-form";
import { useWatch } from "react-hook-form";
import { IResourceComponentsProps } from "@refinedev/core";
import React, { useEffect } from "react";

export const DiscountCreate: React.FC<IResourceComponentsProps> = () => {
  const {
    saveButtonProps,
    refineCore: { formLoading },
    register,
    control,
    formState: { errors },
    setValue,
  } = useForm();

  // Watch for discount type changes
  const discountType = useWatch({
    control,
    name: "type",
    defaultValue: "percentage",
  });

  // Watch for applies to products (ensure it's always an array)
  const appliesToValue = useWatch({
    control,
    name: "appliesTo",
    defaultValue: [],
  });

  const currencies = [
    { value: "usd", label: "USD - US Dollar" },
    { value: "eur", label: "EUR - Euro" },
    { value: "gbp", label: "GBP - British Pound" },
    { value: "cad", label: "CAD - Canadian Dollar" },
  ];

  // Sample product list - In a real app, this would come from an API
  const availableProducts = [
    { id: "prod_premium", name: "Premium Plan" },
    { id: "prod_basic", name: "Basic Plan" },
    { id: "prod_enterprise", name: "Enterprise Plan" },
  ];

  // Register the appliesTo field with default value
  useEffect(() => {
    register("appliesTo", { value: [] });
  }, [register]);

  return (
    <Create isLoading={formLoading} saveButtonProps={saveButtonProps}>
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column" }}
        autoComplete="off"
      >
        <Grid container spacing={2}>
          {/* Name */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <TextField
                  {...register("name", {
                    required: "This field is required",
                  })}
                  label="Name"
                  fullWidth
                  error={!!errors.name}
                  helperText={errors.name?.message as string}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Description */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <TextField
                  {...register("description")}
                  label="Description"
                  fullWidth
                  multiline
                  rows={3}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Discount Type */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <FormControl fullWidth>
                  <InputLabel>Discount Type</InputLabel>
                  <Select
                    {...register("type", {
                      required: "This field is required",
                    })}
                    label="Discount Type"
                    defaultValue="percentage"
                    error={!!errors.type}
                  >
                    <MenuItem value="percentage">Percentage</MenuItem>
                    <MenuItem value="amount">Fixed Amount</MenuItem>
                  </Select>
                </FormControl>
              </CardContent>
            </Card>
          </Grid>

          {/* Percentage Field (conditional) */}
          {discountType === "percentage" && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <TextField
                    {...register("percentage", {
                      required: discountType === "percentage" ? "This field is required" : false,
                      min: { value: 0, message: "Percentage must be at least 0" },
                      max: { value: 100, message: "Percentage must be at most 100" },
                    })}
                    label="Percentage Off"
                    type="number"
                    fullWidth
                    error={!!errors.percentage}
                    helperText={errors.percentage?.message as string}
                    InputProps={{
                      endAdornment: "%"
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Amount and Currency Fields (conditional) */}
          {discountType === "amount" && (
            <>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <TextField
                      {...register("amount", {
                        required: discountType === "amount" ? "This field is required" : false,
                        min: { value: 1, message: "Amount must be at least 1" },
                      })}
                      label="Amount Off"
                      type="number"
                      fullWidth
                      error={!!errors.amount}
                      helperText={errors.amount?.message as string}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <FormControl fullWidth>
                      <InputLabel>Currency</InputLabel>
                      <Select
                        {...register("currency", {
                          required: discountType === "amount" ? "This field is required" : false,
                        })}
                        label="Currency"
                        defaultValue="usd"
                        error={!!errors.currency}
                      >
                        {currencies.map((currency) => (
                          <MenuItem key={currency.value} value={currency.value}>
                            {currency.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </CardContent>
                </Card>
              </Grid>
            </>
          )}

          {/* Expiration Date */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <TextField
                  {...register("redeemBy")}
                  label="Expiration Date"
                  type="datetime-local"
                  fullWidth
                  disabled={discountType === "amount"}
                  error={!!errors.redeemBy}
                  helperText={
                    discountType === "amount"
                      ? "Expiration date is not available for fixed amount discounts"
                      : (errors.redeemBy?.message as string || "Leave empty for no expiration")
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Max Redemptions */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <TextField
                  {...register("maxRedemptions", {
                    min: { value: 1, message: "Max redemptions must be at least 1" },
                  })}
                  label="Max Redemptions"
                  type="number"
                  fullWidth
                  error={!!errors.maxRedemptions}
                  helperText={errors.maxRedemptions?.message as string || "Leave empty for unlimited uses"}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Applies To Products */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <FormControl fullWidth>
                  <InputLabel>Applies to Products (Optional)</InputLabel>
                  <Select
                    name="appliesTo"
                    value={appliesToValue || []}
                    onChange={(event) => {
                      const value = event.target.value;
                      setValue("appliesTo", typeof value === 'string' ? value.split(',') : value);
                    }}
                    multiple
                    input={<OutlinedInput label="Applies to Products (Optional)" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as string[]).map((value) => {
                          const product = availableProducts.find(p => p.id === value);
                          return (
                            <Chip key={value} label={product?.name || value} />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {availableProducts.map((product) => (
                      <MenuItem key={product.id} value={product.id}>
                        {product.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </CardContent>
            </Card>
          </Grid>

          {/* Active Status */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <FormControlLabel
                  control={
                    <Switch
                      {...register("active")}
                      defaultChecked
                    />
                  }
                  label="Active"
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Create>
  );
};
